import Container from "@/components/Container/Container";
import Section from "@/components/Section/Section";
import PageHero from "@/components/PageHero/PageHero";
import CTASection from "@/components/CTASection/CTASection";
import styles from "./WorkWithPage.module.css";

const groups = [
  {
    id: "individuals",
    eyebrow: "Individuals",
    title: "For adults seeking deeper understanding and lasting change.",
    body: "I work with adults who want to better understand themselves, heal from past experiences, and create more fulfilling relationships and lives. Many of my clients are thoughtful, reflective people who have already done some personal work but feel stuck in certain patterns.",
    listIntro: "Clients often seek therapy when they are experiencing:",
    bullets: [
      "Anxiety or chronic stress",
      "Trauma or unresolved past experiences",
      "Major life transitions",
      "Relationship challenges",
      "Burnout or emotional exhaustion",
      "Feeling disconnected from themselves or others",
    ],
    closing:
      "In therapy we work together to understand the deeper patterns behind these experiences and create meaningful and lasting change.",
  },
  {
    id: "couples",
    eyebrow: "Couples",
    title: "Slow down, understand each other clearly, and rebuild connection.",
    body: "Relationships can be one of the most meaningful—and most challenging—parts of our lives. Couples therapy provides a space to slow down, understand each other more clearly, and rebuild connection.",
    listIntro: "In couples work we often explore:",
    bullets: [
      "Communication challenges",
      "Recurring conflicts or arguments",
      "Emotional disconnection",
      "Navigating major life transitions",
      "Trust and rebuilding safety",
    ],
    closing:
      "My approach focuses on helping couples understand the patterns that keep them stuck while developing new ways of communicating and supporting one another.",
  },
  {
    id: "therapists",
    eyebrow: "Therapists & Clinicians",
    title: "A reflective space for the people who hold space for others.",
    body: "I also work with therapists seeking consultation, supervision, or personal therapy. Clinicians often carry a great deal of responsibility and emotional labor, and having a space for reflection and professional development is essential.",
    listIntro: "Consultation may focus on:",
    bullets: [
      "Case conceptualization",
      "Trauma-informed care",
      "Somatic and relational interventions",
      "Navigating ethical or complex clinical situations",
      "Professional growth and identity development",
    ],
  },
] as const;

export default function WorkWithPage() {
  return (
    <>
      <PageHero
        eyebrow="Who I work with"
        title="Therapy and consultation for individuals, couples, and clinicians."
        intro="I believe people are capable of meaningful change when they feel understood, supported, and connected to their own internal wisdom."
        accent="bloom"
      />

      <Section spacing="loose">
        <Container width="default">
          <nav className={styles.anchorNav} aria-label="On this page">
            {groups.map((g) => (
              <a key={g.id} href={`#${g.id}`} className={styles.anchor}>
                {g.eyebrow}
              </a>
            ))}
          </nav>

          <div className={styles.groups}>
            {groups.map((g) => (
              <section key={g.id} id={g.id} className={styles.group}>
                <span className="eyebrow">{g.eyebrow}</span>
                <h2 className={styles.groupTitle}>{g.title}</h2>
                <p>{g.body}</p>
                <p className={styles.listIntro}>{g.listIntro}</p>
                <ul className={styles.bullets}>
                  {g.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                {"closing" in g && g.closing ? (
                  <p className={styles.closing}>{g.closing}</p>
                ) : null}
              </section>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
